import React, { useMemo } from 'react';
import { toast } from 'react-toastify';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getError } from '@utils/graph-utils';

import { IBaseDrawerProps } from '../types';

import FormattingService from 'src/core/services/FormattingService';
import PrecisionService from 'src/core/services/PrecisionService';
import { useGetCurrencies, useModifyTaxation } from '@common/slices';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { useGetTaxationDetails } from '@features/configurations/taxation/taxation.slice';
import { modifyTaxationDrawerSections } from '@common/constants';
import { RootState } from '@core/redux/store';
import { useSelector } from 'react-redux';
interface Props extends IBaseDrawerProps {
  taxationId?: string;
}
const ModifyTaxationDrawer: React.FC<Props> = ({ taxationId, onSuccess }) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const { data: taxationResp } = useGetTaxationDetails({
    variables: { id: taxationId }
  });

  const { data: currenciesResp } = useGetCurrencies.useQuery();
  const [modifyTaxation] = useModifyTaxation();

  const currencyOptions = useMemo<Record<string, string>>(() => {
    const arr = currenciesResp?.core?.getCurrencies ?? [];
    return arr.reduce((acc: Record<string, string>, c: any) => {
      acc[c.id] = c.title;
      return acc;
    }, {});
  }, [currenciesResp]);

  const taxationEntity = taxationResp?.core?.getTaxationDetails;

  const initialValues = useMemo(() => {
    const t = taxationEntity;
    const symbol = t?.fixedStampCurrencyValue?.symbol;

    const fixedStampWithSymbol =
      t?.fixedStamp != null && symbol
        ? `${symbol} ${FormattingService.formatNumberWithCommas(t.fixedStamp)}`
        : (t?.fixedStamp?.toString() ?? '');

    return {
      lineID: t?.line?.id ? [t.line.id] : [],
      effectiveFrom: t?.effectiveFrom ?? '',
      effectiveTo: t?.effectiveTo ?? '',
      fixedStamp: t?.fixedStamp?.toString() ?? '',
      fixedStampCurrency: t?.fixedStampCurrency ? [t.fixedStampCurrency] : [],
      proportionalStamp: PrecisionService.multiplyBy100(t?.proportionalStamp),
      municipalityTax: PrecisionService.multiplyBy100(t?.municipalityTax),
      taxOnCommission: PrecisionService.multiplyBy100(t?.taxOnCommission),
      reinsuranceTax: PrecisionService.multiplyBy100(t?.reinsuranceTax),
      fixedStampWithSymbol
    };
  }, [taxationEntity]);

  const lineOption = useMemo<Record<string, string>>(() => {
    const t = taxationEntity;
    if (!t?.line?.id) return {};
    return { [t.line.id]: `${t.line.code} - ${t.line.name}` };
  }, [taxationEntity]);

  const sections = useMemo<IDynamicFormWidgetSection[]>(() => {
    return modifyTaxationDrawerSections(
      {
        lineID: initialValues.lineID,
        effectiveFrom: initialValues.effectiveFrom,
        effectiveTo: initialValues.effectiveTo,
        fixedStamp: initialValues.fixedStamp,
        fixedStampCurrency: initialValues.fixedStampCurrency,
        proportionalStamp: String(initialValues.proportionalStamp),
        municipalityTax: String(initialValues.municipalityTax),
        taxOnCommission: String(initialValues.taxOnCommission),
        reinsuranceTax: String(initialValues.reinsuranceTax)
      },
      lineOption,
      currencyOptions
    );
  }, [initialValues, lineOption, currencyOptions]);

  const submitForm = async (rawValues: Record<string, any>) => {
    const [values] = normalizeDynamicFormDrawerValues(sections, rawValues);

    try {
      const taxationInput = {
        id: taxationId,
        lineID: Array.isArray(values.lineID) ? values.lineID[0] : values.lineID,
        effectiveFrom: values.effectiveFrom
          ? new Date(values.effectiveFrom).toISOString()
          : '',
        effectiveTo: values.effectiveTo
          ? new Date(values.effectiveTo).toISOString()
          : '',
        fixedStamp: values.fixedStamp ? parseFloat(values.fixedStamp) : null,
        fixedStampCurrency: Array.isArray(values.fixedStampCurrency)
          ? values.fixedStampCurrency[0]
          : values.fixedStampCurrency,
        proportionalStamp: PrecisionService.divideBy100(
          values.proportionalStamp
        ),
        municipalityTax: PrecisionService.divideBy100(values.municipalityTax),
        taxOnCommission: PrecisionService.divideBy100(values.taxOnCommission),
        reinsuranceTax: PrecisionService.divideBy100(values.reinsuranceTax)
      };

      const res = await modifyTaxation({ taxationInput: taxationInput });

      if (!res.errors) {
        onSuccess();
      }
    } catch (err) {
      toast.error(
        <ToastErrorMessage>
          {getError(err) || DEFAULT_ERROR_TEXT}
        </ToastErrorMessage>
      );
    }
  };

  return (
    <DynamicFormWidget
      sections={sections}
      onSubmit={submitForm}
      submitButtonState={submitButtonState}
    />
  );
};

export { ModifyTaxationDrawer };
